// src/components/cart/StickerCartLine/StickerCartLine.jsx
//
// ОДНА ПОЗИЦИЯ СТИКЕРА В КОРЗИНЕ (п.87, сессия 2026-10-29).
//
// 🔑 ЗАЧЕМ ОТДЕЛЬНЫЙ КОМПОНЕНТ.
//    Товарная корзина рисуется инлайном в `Cart.jsx` и ожидает
//    `item.Product.name` и фото. У стикера нет ни товара, ни фото товара:
//    есть макет карточки или картинка клиента, размер, кромка и
//    «макет ждём». Встроить его в тот же цикл значило бы превратить
//    каждую строку разметки в развилку «товар или стикер».
//
// ⚠️ ЧЕМ ПОКАЗЫВАТЬ КАРТИНКУ КЛИЕНТА.
//    Файл лежит в приватной зоне и отдаётся ТОЛЬКО владельцу строки
//    корзины (`GET /sticker-cart/:id/image`). Поэтому `<img src>` ведёт
//    на API с `?session_id=` — у `<img>` нет возможности поставить
//    заголовок `x-session-id`. Чужой id картинку не покажет: сервер
//    проверяет владельца.
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateStickerCartItem,
  removeStickerCartItem,
} from '../../../store/stickerCartSlice.js';
import { getAnonymousId } from '../../../utils/anonymousId.js';
import { customImageUrl } from '../../../api/stickerCart.js';

const SIZES = [50, 70, 100];

const fmt = (mm) => `${Math.round(Number(mm) / 10)} см`;

export const StickerCartLine = ({ item, onChanged }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // «Макет ждём» — самый важный признак в этой строке: без него менеджер
  // увидит позицию и решит, что печатать можно.
  const isPending = item.design_status === 'pending';
  const hasCustom = item.has_custom_image;
  const preview = hasCustom
    ? customImageUrl(item.id, getAnonymousId())
    : item.preview;

  const changeQty = async (next) => {
    if (next < 1 || busy) return;
    setBusy(true);
    setErr('');
    try {
      await dispatch(updateStickerCartItem({ id: item.id, data: { quantity: next } })).unwrap();
      onChanged?.();
    } catch (e) {
      setErr(typeof e === 'string' ? e : 'Не удалось изменить количество');
    } finally {
      setBusy(false);
    }
  };

  const changeSize = async (mm) => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      // ⚠️ Кромку пересчитывает СЕРВЕР (2 мм, но ≤10 %): передавать её
      //    отсюда значило бы дать клиенту влиять на деталь заказа.
      await dispatch(updateStickerCartItem({ id: item.id, data: { size_mm: mm } })).unwrap();
      onChanged?.();
    } catch (e) {
      setErr(typeof e === 'string' ? e : 'Не удалось изменить размер');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr('');
    try {
      await dispatch(removeStickerCartItem(item.id)).unwrap();
      onChanged?.();
    } catch (e) {
      setErr(typeof e === 'string' ? e : 'Не удалось удалить позицию');
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 18,
        padding: 16,
        alignItems: 'flex-start',
        backgroundColor: 'rgb(var(--c-surface))',
        border: '1px solid rgb(var(--c-border))',
        borderRadius: 16,
        marginBottom: 12,
      }}
    >
      {/* КАРТИНКА: макет карточки или файл клиента; пока нет — понятная заглушка */}
      <div
        style={{
          width: 96,
          height: 96,
          borderRadius: 10,
          flexShrink: 0,
          overflow: 'hidden',
          background: 'rgb(var(--c-surface-2))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          color: 'rgb(var(--c-text-secondary))',
          textAlign: 'center',
          padding: 6,
          boxSizing: 'border-box',
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt={item.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          'Картинку ждём'
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'rgb(var(--c-text))' }}>
          {item.title}
        </div>
        <div style={{ fontSize: 12, color: 'rgb(var(--c-text-secondary))', marginTop: 3 }}>
          Размер: {fmt(item.size_mm)} · кромка {Number(item.edge_mm).toFixed(1)} мм · наклейка
        </div>

        {/* РАЗМЕР — меняется прямо здесь: клиент ошибся одним нажатием */}
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {SIZES.map((mm) => {
            const on = Number(item.size_mm) === mm;
            return (
              <button
                key={mm}
                type="button"
                disabled={busy}
                onClick={() => changeSize(mm)}
                style={{
                  border: `1px solid ${on ? 'rgb(var(--c-accent))' : 'rgb(var(--c-border))'}`,
                  background: on ? 'rgb(var(--c-accent))' : 'transparent',
                  color: on ? '#fff' : 'rgb(var(--c-text-secondary))',
                  borderRadius: 7,
                  padding: '4px 10px',
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                {fmt(mm)}
              </button>
            );
          })}
        </div>

        {isPending && (
          <div
            style={{
              marginTop: 8,
              padding: '6px 9px',
              borderRadius: 7,
              background: 'rgb(var(--c-accent-soft))',
              color: 'rgb(var(--c-text-secondary))',
              fontSize: 11.5,
              lineHeight: 1.45,
            }}
          >
            <b style={{ color: 'rgb(var(--c-text))' }}>Макет ждём</b> — картинка ещё не
            приложена. Заказ оформится, печать начнётся, когда вы её пришлёте.
          </div>
        )}

        {err && (
          <div style={{ marginTop: 6, fontSize: 11.5, color: 'rgb(var(--c-danger))' }}>{err}</div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--c-accent))' }}>
            {Number(item.unit_price).toLocaleString('ru-RU')} ₽ / шт
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => changeQty(item.quantity - 1)}
              disabled={busy}
              aria-label="меньше"
              style={{
                width: 32, height: 32, borderRadius: '50%',
                border: '1px solid rgb(var(--c-border))',
                background: 'rgb(var(--c-surface))',
                color: 'rgb(var(--c-text))',
                cursor: 'pointer',
              }}
            >
              −
            </button>
            <b style={{ minWidth: 24, textAlign: 'center' }}>{item.quantity}</b>
            <button
              type="button"
              onClick={() => changeQty(item.quantity + 1)}
              disabled={busy}
              aria-label="больше"
              style={{
                width: 32, height: 32, borderRadius: '50%',
                border: '1px solid rgb(var(--c-border))',
                background: 'rgb(var(--c-surface))',
                color: 'rgb(var(--c-text))',
                cursor: 'pointer',
              }}
            >
              +
            </button>
          </div>
          <div style={{ minWidth: 90, textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'rgb(var(--c-text))' }}>
              {Number(item.line_total).toLocaleString('ru-RU')} ₽
            </div>
            <div style={{ fontSize: 11, color: 'rgb(var(--c-text-secondary))' }}>за позицию</div>
          </div>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgb(var(--c-text-secondary))',
              fontSize: 18,
              cursor: 'pointer',
              padding: 8,
            }}
            aria-label="Удалить позицию"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickerCartLine;
